import { useState, useEffect, useCallback } from 'react'
import { asistenteApi } from '../api/asistente'
import { colors, text, space, radius, sx } from '../design/tokens'
import { fmtFechaHora } from '../utils/fecha'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import Paginador from '../components/Paginador'

// SISTEMA → Conversaciones del bot (Asistente LGR 2, 4 oct 2026). Las leen
// administradores y las personas habilitadas (Diego Gorfinkiel). Se guardan
// 60 días. Sirve para ver qué pregunta la gente, qué hizo el bot y dónde se trabó.

const FILTROS_VACIOS = { q: '', usuario_id: '', escaladas: false, con_acciones: false }

const fmtUsd = (v) => `US$ ${Number(v || 0).toFixed(2).replace('.', ',')}`

export default function AsistenteConversacionesPage() {
  const [datos, setDatos] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [filtros, setFiltros] = useState(FILTROS_VACIOS)
  const [pagina, setPagina] = useState(1)
  const [abierta, setAbierta] = useState(null)

  const cargar = useCallback(async () => {
    setCargando(true)
    setError('')
    try {
      const params = { page: pagina }
      if (filtros.q) params.q = filtros.q
      if (filtros.usuario_id) params.usuario_id = filtros.usuario_id
      if (filtros.escaladas) params.escaladas = 1
      if (filtros.con_acciones) params.con_acciones = 1
      const { data } = await asistenteApi.conversaciones(params)
      setDatos(data)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudieron cargar las conversaciones.')
    } finally {
      setCargando(false)
    }
  }, [filtros, pagina])

  useEffect(() => { cargar() }, [cargar])

  const set = (k, v) => { setFiltros(f => ({ ...f, [k]: v })); setPagina(1) }

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.titulo}>Conversaciones del bot</h2>
        {datos && <span style={sx.chipEstado}>Se guardan {datos.dias_guardado} días</span>}
      </div>

      {error && <div style={st.error}>{error}</div>}

      {datos?.por_usuario?.length > 0 && (
        <div style={{ ...st.tabla, marginBottom: space.xl }}>
          <div style={{ padding: `${space.lg}px ${space.xl}px` }}>
            <h3 style={sx.cardTitle}>Últimos 30 días, por persona</h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>Persona</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Conversaciones</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Mensajes</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Acciones</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Escaladas</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Costo IA</th>
                </tr>
              </thead>
              <tbody>
                {datos.por_usuario.map(p => (
                  <tr key={p.usuario_id} style={st.fila} title="Ver sus conversaciones"
                    onMouseEnter={hoverOn} onMouseLeave={hoverOff}
                    onClick={() => set('usuario_id', String(p.usuario_id))}>
                    <td style={sx.td}>{p.nombre || `#${p.usuario_id}`}</td>
                    <td style={{ ...sx.td, textAlign: 'right' }}>{p.conversaciones}</td>
                    <td style={{ ...sx.td, textAlign: 'right' }}>{p.mensajes}</td>
                    <td style={{ ...sx.td, textAlign: 'right' }}>{p.acciones}</td>
                    <td style={{ ...sx.td, textAlign: 'right', fontWeight: p.escaladas ? 600 : 400, color: p.escaladas ? colors.errStrong : colors.textMeta }}>{p.escaladas}</td>
                    <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtUsd(p.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={sx.filterBar}>
        <input style={{ ...sx.input, minWidth: 220 }} placeholder="Buscar en las conversaciones…"
          value={filtros.q} onChange={e => set('q', e.target.value)} />
        <select style={sx.input} value={filtros.usuario_id} onChange={e => set('usuario_id', e.target.value)}>
          <option value="">Todas las personas</option>
          {(datos?.por_usuario || []).map(p => <option key={p.usuario_id} value={p.usuario_id}>{p.nombre}</option>)}
        </select>
        <button type="button" style={{ ...sx.pill, ...(filtros.escaladas ? sx.pillActive : {}) }}
          onClick={() => set('escaladas', !filtros.escaladas)}>Escaladas</button>
        <button type="button" style={{ ...sx.pill, ...(filtros.con_acciones ? sx.pillActive : {}) }}
          onClick={() => set('con_acciones', !filtros.con_acciones)}>Con acciones</button>
        <Boton variante="ghost" onClick={() => { setFiltros(FILTROS_VACIOS); setPagina(1) }}>Limpiar</Boton>
      </div>

      <div style={st.tabla}>
        {cargando && !datos ? (
          <p style={st.vacio}>Cargando…</p>
        ) : !datos?.data?.length ? (
          <p style={st.vacio}>No hay conversaciones con estos filtros.</p>
        ) : (
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Última actividad</th>
                <th style={sx.th}>Persona</th>
                <th style={sx.th}>Canal</th>
                <th style={sx.th}>Primera pregunta</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Mensajes</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Acciones</th>
                <th style={sx.th}>Estado</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Costo IA</th>
              </tr>
            </thead>
            <tbody>
              {datos.data.map(c => (
                <tr key={c.id} style={st.fila} title="Ver la conversación" onClick={() => setAbierta(c.id)}
                  onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                  <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{fmtFechaHora(c.updated_at)}</td>
                  <td style={{ ...sx.td, whiteSpace: 'nowrap' }}>{c.usuario?.nombre || '—'}</td>
                  <td style={sx.td}><span style={{ ...sx.badge, ...st.badgeCanal }}>{c.canal}</span></td>
                  <td style={{ ...sx.td, maxWidth: 420 }}><div style={st.pregunta}>{c.primera_pregunta || '—'}</div></td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>{c.turnos}</td>
                  <td style={{ ...sx.td, textAlign: 'right' }}>{c.acciones}</td>
                  <td style={sx.td}>
                    {c.escalada && <span style={{ ...sx.badge, background: colors.errBg, color: colors.err }}>Escalada</span>}
                    {!c.escalada && c.con_error && <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>Con error</span>}
                    {!c.escalada && !c.con_error && <span style={sx.placeholder}>—</span>}
                  </td>
                  <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{fmtUsd(c.usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {datos && <Paginador pagina={datos.pagina} totalPaginas={datos.paginas} onCambiar={setPagina} />}

      {abierta && <DetalleConversacion id={abierta} onClose={() => setAbierta(null)} />}
    </div>
  )
}

function DetalleConversacion({ id, onClose }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    asistenteApi.conversacion(id)
      .then(({ data }) => setData(data))
      .catch(err => setError(err.response?.data?.message || 'No se pudo cargar la conversación.'))
  }, [id])

  const c = data?.conversacion

  return (
    <Modal title={c ? `Conversación de ${c.usuario?.nombre || '—'} (${c.canal})` : 'Conversación'} onClose={onClose} maxWidth="680px">
      {error && <div style={st.error}>{error}</div>}
      {!data && !error && <p style={st.vacio}>Cargando…</p>}
      {c && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
          <div style={st.meta}>
            Empezó {fmtFechaHora(c.created_at)} · última actividad {fmtFechaHora(c.updated_at)} · {c.turnos} mensajes · {c.acciones} acciones · {fmtUsd(c.usd)}
          </div>
          {(c.mensajes || []).flatMap((m, i) => bloques(m).map((b, j) => <Bloque key={`${i}-${j}`} rol={m.role} b={b} />))}

          {data.confirmaciones?.length > 0 && (
            <div style={{ marginTop: space.lg }}>
              <h3 style={{ ...sx.cardTitle, marginBottom: space.md }}>Acciones propuestas</h3>
              <table style={sx.table}>
                <thead>
                  <tr>
                    <th style={sx.th}>Código</th>
                    <th style={sx.th}>Acción</th>
                    <th style={sx.th}>Estado</th>
                    <th style={sx.th}>Propuesta</th>
                  </tr>
                </thead>
                <tbody>
                  {data.confirmaciones.map(k => (
                    <tr key={k.codigo} style={st.filaFija}>
                      <td style={{ ...sx.td, ...sx.mono }}>{k.codigo}</td>
                      <td style={{ ...sx.td, ...sx.mono, fontSize: text.sec, color: colors.textSub }}>{k.tool}</td>
                      <td style={sx.td}><span style={{ ...sx.badge, ...(ESTADO_CONF[k.estado] || {}) }}>{k.estado}</span></td>
                      <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{fmtFechaHora(k.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

const ESTADO_CONF = {
  ejecutada: { background: colors.okBg, color: colors.ok },
  fallida: { background: colors.errBg, color: colors.err },
  cancelada: { background: colors.bgSoft, color: colors.textSec },
  pendiente: { background: colors.warnBg, color: colors.warn },
  ejecutando: { background: colors.infoBg, color: colors.info },
}

// Un mensaje de la API puede traer texto, llamadas a herramientas y resultados
function bloques(m) {
  if (typeof m.content === 'string') return [{ tipo: 'texto', texto: m.content }]
  return (m.content || []).map(b => {
    if (b.type === 'text') return { tipo: 'texto', texto: b.text }
    if (b.type === 'tool_use') return { tipo: 'tool', texto: `${b.name} ${JSON.stringify(b.input || {})}` }
    if (b.type === 'tool_result') return { tipo: 'resultado', texto: typeof b.content === 'string' ? b.content : JSON.stringify(b.content) }
    return { tipo: 'texto', texto: `[${b.type}]` }
  }).filter(b => b.texto && b.texto.trim() !== '')
}

function Bloque({ rol, b }) {
  const [abierto, setAbierto] = useState(false)
  if (b.tipo === 'tool') return <div style={st.tool}>🔧 {b.texto}</div>
  if (b.tipo === 'resultado') {
    return (
      <button type="button" style={st.resultado} onClick={() => setAbierto(!abierto)}>
        {abierto ? b.texto : `↳ resultado (${b.texto.length} caracteres) — ver`}
      </button>
    )
  }
  return <div style={{ ...st.burbuja, ...(rol === 'user' ? st.burbujaUser : st.burbujaBot) }}>{b.texto}</div>
}

const hoverOn = e => { e.currentTarget.style.background = colors.bgSoft }
const hoverOff = e => { e.currentTarget.style.background = '' }

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl },
  titulo: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  error: { background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, marginBottom: space.lg, fontSize: text.body },
  tabla: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  fila: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44, cursor: 'pointer', transition: 'background 0.1s' },
  filaFija: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44 },
  vacio: { padding: space['3xl'], textAlign: 'center', color: colors.textMeta, fontSize: text.body, margin: 0 },
  pregunta: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badgeCanal: { background: colors.zonaBg, color: colors.zonaText },
  meta: { fontSize: text.sec, color: colors.textSec },
  burbuja: { maxWidth: '85%', padding: `${space.md}px ${space.lg}px`, borderRadius: radius.lg, fontSize: text.body, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  burbujaUser: { alignSelf: 'flex-end', background: colors.linkBg, color: colors.text, border: `1px solid ${colors.linkBorder}` },
  burbujaBot: { alignSelf: 'flex-start', background: colors.bgSoft, color: colors.text, border: `1px solid ${colors.border}` },
  tool: { alignSelf: 'flex-start', fontFamily: sx.mono.fontFamily, fontSize: text.meta, color: colors.textSec, wordBreak: 'break-all' },
  resultado: { alignSelf: 'flex-start', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sx.mono.fontFamily, fontSize: text.meta, color: colors.textMeta, wordBreak: 'break-all', whiteSpace: 'pre-wrap', maxWidth: '100%' },
}
